import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useSession } from "@/context/SessionContext";
import { VoiceInput } from "@/components/VoiceInput";
import { TasteAppreciationModal } from "@/components/TasteAppreciationModal";

const QUESTIONS = [
  { key: "liked", prompt: "What drew you to the spaces you picked?", hint: "The light, the textures, the calm… say it however it comes.", type: "text" },
  { key: "people", prompt: "Who will be living in — or using — this space?", type: "chips", options: ["Me", "My partner", "Our kids", "My parents", "Guests, often", "Team / colleagues", "Pets"], custom: true },
  { key: "decision_makers", prompt: "Who else gets a say in how it looks?", hint: "So we know whose taste to weave in.", type: "chips", options: ["Just me", "My partner", "My parents", "My kids", "A friend", "Business partner"], custom: true },
  { key: "feel", prompt: "When you walk in, how should it feel?", type: "chips", options: ["Calm", "Warm & cosy", "Airy", "Luxurious", "Playful", "Sharp & minimal"] },
  { key: "avoid", prompt: "Anything you absolutely don't want?", hint: "Colours, clutter, a style your partner loves and you don't…", type: "text" },
  { key: "budget", prompt: "Where does your budget comfortably sit?", type: "chips", options: ["Essentials first", "Balanced", "Premium", "Money is no object"], single: true },
  { key: "timeline", prompt: "When would you like to begin?", type: "chips", options: ["Right away", "In 1–3 months", "3–6 months", "Just exploring"], single: true },
];

function deriveRooms(session, people) {
  const has = (re) => people.some((p) => re.test(p));
  if (session.project_type === "office") return ["Reception", "Workstations", "Cabin", "Meeting room", "Pantry", "Lounge"];
  if (session.project_type === "commercial") return ["Entrance", "Main floor", "Counter / Bar", "Seating area", "Back office", "Washrooms"];
  const rooms = ["Living room"];
  rooms.push(has(/partner/i) ? "Our bedroom" : "My bedroom");
  if (has(/kids/i)) rooms.push("Kids' room");
  if (has(/parents/i)) rooms.push("Parents' room");
  const used = rooms.length - 1;
  if ((session.bedrooms || 0) > used) rooms.push("Guest bedroom");
  (session.extra_rooms || []).forEach((r) => !rooms.includes(r) && rooms.push(r));
  if (!rooms.includes("Kitchen")) rooms.push("Kitchen");
  if (!rooms.includes("Dining")) rooms.push("Dining");
  return rooms;
}

export default function Conversation() {
  const navigate = useNavigate();
  const { session, update, refresh } = useSession();
  const [i, setI] = useState(0);
  const [answers, setAnswers] = useState(session.answers || {});
  const [custom, setCustom] = useState("");
  const [dir, setDir] = useState(1);
  const [done, setDone] = useState(false);
  const [compliment, setCompliment] = useState(null);

  const q = QUESTIONS[i];
  const val = answers[q.key] ?? (q.type === "chips" ? [] : "");
  const setVal = (v) => setAnswers({ ...answers, [q.key]: v });
  const toggleChip = (o) => {
    if (q.single) return setVal([o]);
    setVal(val.includes(o) ? val.filter((x) => x !== o) : [...val, o]);
  };
  const valid = q.type === "chips" ? val.length > 0 : String(val).trim().length > 1;

  const go = (d) => { setDir(d); setI(i + d); setCustom(""); };

  const finish = async () => {
    const people = Array.from(new Set([...(answers.people || []), ...(answers.decision_makers || [])]));
    const rooms = deriveRooms(session, people);
    setDone(true);
    await update({ answers, people, rooms, step: Math.max(session.step, 4) });
    try {
      const { data } = await api.post(`/sessions/${session.id}/appreciate`);
      setCompliment(data);
    } catch {
      setCompliment({ headline: "Oh lovely — you have a sweet taste.", note: "Calm, considered and quietly confident. We already see the space taking shape." });
    }
  };

  const continueOn = async () => { await refresh(); navigate("/furniture"); };

  return (
    <main className="max-w-3xl mx-auto px-6 sm:px-10 pt-12 pb-32 min-h-[80vh] flex flex-col">
      <p className="eyebrow">Step four · About you</p>
      <div className="flex gap-1.5 mt-5">
        {QUESTIONS.map((_, k) => <span key={k} className={`h-1 flex-1 rounded-full transition-colors duration-500 ${k <= i ? "bg-neutral-900" : "bg-neutral-200"}`} />)}
      </div>

      <div className="flex-1 flex flex-col justify-center mt-10">
        <AnimatePresence mode="wait" custom={dir}>
          <motion.div
            key={q.key}
            data-testid={`question-card-${q.key}`}
            initial={{ opacity: 0, x: 40 * dir }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 * dir }}
            transition={{ duration: 0.35, ease: [0.2, 0.7, 0.2, 1] }}
            className="rounded-[2rem] border border-neutral-200 bg-white p-8 sm:p-12 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.25)]"
          >
            <span className="font-mono text-xs text-neutral-400">{String(i + 1).padStart(2, "0")} / {String(QUESTIONS.length).padStart(2, "0")}</span>
            <h2 data-testid="question-prompt" className="font-display font-light text-3xl sm:text-5xl leading-[1.1] mt-4">{q.prompt}</h2>
            {q.hint && <p className="text-neutral-500 mt-3">{q.hint}</p>}

            {q.type === "text" ? (
              <div className="mt-10 flex items-end gap-4">
                <textarea
                  data-testid={`answer-input-${q.key}`}
                  value={val}
                  onChange={(e) => setVal(e.target.value)}
                  rows={3}
                  placeholder="Type, or tap the mic and just talk…"
                  className="flex-1 resize-none bg-transparent border-b border-neutral-300 focus:border-neutral-900 focus:outline-none text-lg sm:text-xl font-light py-2 placeholder:text-neutral-300 transition-colors"
                />
                <VoiceInput onText={(t) => setVal(val ? `${val} ${t}` : t)} />
              </div>
            ) : (
              <div className="mt-10">
                <div className="flex flex-wrap gap-2">
                  {[...q.options, ...val.filter((v) => !q.options.includes(v))].map((o) => (
                    <button key={o} data-testid={`chip-${q.key}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => toggleChip(o)} className={`chip ${val.includes(o) ? "chip-on" : ""}`}>{o}</button>
                  ))}
                </div>
                {q.custom && (
                  <form className="mt-4 flex gap-2" onSubmit={(e) => { e.preventDefault(); custom.trim() && (toggleChip(custom.trim()), setCustom("")); }}>
                    <input data-testid={`custom-chip-input-${q.key}`} value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Someone else? Add a name…" className="flex-1 h-10 rounded-full border border-neutral-300 px-4 text-sm focus:outline-none focus:border-neutral-900" />
                    <button data-testid={`custom-chip-add-${q.key}`} disabled={!custom.trim()} className="chip chip-on disabled:opacity-30">Add</button>
                  </form>
                )}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-between mt-8">
          <button data-testid="question-back-button" onClick={() => go(-1)} disabled={i === 0} className="btn-ghost h-12 disabled:opacity-0"><ArrowLeft size={16} /> Back</button>
          {i < QUESTIONS.length - 1 ? (
            <button data-testid="question-next-button" onClick={() => go(1)} disabled={!valid} className="btn-ink h-12">Next <ArrowRight size={16} /></button>
          ) : (
            <button data-testid="question-finish-button" onClick={finish} disabled={!valid} className="btn-ink h-12">That's me <ArrowRight size={16} /></button>
          )}
        </div>
      </div>

      <TasteAppreciationModal open={done} loading={!compliment} compliment={compliment} onContinue={continueOn} />
    </main>
  );
}
